import { useState, useEffect } from 'react';
import { Eye, ThumbsUp, MessageSquare, Share, MousePointer, BarChart3 } from 'lucide-react';
import { contentApi, type ItemAnalyticsDataPoint } from '../../api/content';

interface ItemAnalyticsCardProps {
  projectId: string;
  itemId: string;
}

export function ItemAnalyticsCard({ projectId, itemId }: ItemAnalyticsCardProps) {
  const [dataPoints, setDataPoints] = useState<ItemAnalyticsDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    contentApi.getItemAnalytics(projectId, itemId, 30)
      .then(setDataPoints)
      .catch(() => setDataPoints([]))
      .finally(() => setLoading(false));
  }, [projectId, itemId]);

  // Aggregate metrics from data points
  const metrics = dataPoints.reduce<Record<string, number>>((acc, dp) => {
    acc[dp.metric_type] = (acc[dp.metric_type] || 0) + dp.total;
    return acc;
  }, {});

  const metricItems = [
    { key: 'views', label: 'Views', icon: <Eye size={13} />, color: 'text-blue-500' },
    { key: 'likes', label: 'Likes', icon: <ThumbsUp size={13} />, color: 'text-coral-500' },
    { key: 'comments', label: 'Comments', icon: <MessageSquare size={13} />, color: 'text-violet-500' },
    { key: 'shares', label: 'Shares', icon: <Share size={13} />, color: 'text-emerald-500' },
    { key: 'clicks', label: 'Clicks', icon: <MousePointer size={13} />, color: 'text-amber-500' },
  ];

  const hasAnyData = Object.values(metrics).some(v => v > 0);

  return (
    <div className="bg-white rounded-xl border border-line p-4">
      <h3 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <BarChart3 size={12} />
        Analytics (30d)
      </h3>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center justify-between animate-pulse">
              <div className="h-3 w-16 bg-surface-subtle rounded" />
              <div className="h-3 w-8 bg-surface-subtle rounded" />
            </div>
          ))}
        </div>
      ) : !hasAnyData ? (
        <div className="text-center py-3">
          <p className="text-[11px] text-subtle">No analytics data yet</p>
          <p className="text-[10px] text-subtle mt-0.5">Data populates via Voyager integration</p>
        </div>
      ) : (
        <div className="space-y-2">
          {metricItems.map(item => {
            const value = metrics[item.key] || 0;
            return (
              <div key={item.key} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={item.color}>{item.icon}</span>
                  <span className="text-xs text-muted">{item.label}</span>
                </div>
                <span className="text-xs font-semibold text-text">
                  {value.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
